import { useState } from 'react';

interface ArchitectureLayer {
  id: string;
  title: string;
  subtitle: string;
  capabilities: string[];
  isCore?: boolean;
}

const architectureLayers: ArchitectureLayer[] = [
  {
    id: 'tool',
    title: '工具层',
    subtitle: '效率能力',
    capabilities: [
      '单证自动生成',
      'AI智能归类',
      '自动提交与回执回传'
    ]
  },
  {
    id: 'system',
    title: '系统层',
    subtitle: '管理能力',
    capabilities: [
      '数据归集',
      '规则校验',
      '差异留痕',
      '目录化归档',
      '链路固化'
    ],
    isCore: true
  },
  {
    id: 'enhancement',
    title: '增强层',
    subtitle: '复杂场景支持',
    capabilities: [
      '退税支持',
      '核算支持',
      '值守保障'
    ]
  }
];

export default function PlatformArchitecture() {
  const [hoveredLayer, setHoveredLayer] = useState<string | null>(null);

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* 标题区 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            平台能力架构
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            星贸云以系统层为核心，支撑企业从"跑通流程"到"能力化管理"的升级路径
          </p>
        </div>

        {/* 三层架构卡片 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {architectureLayers.map((layer, index) => (
            <div
              key={layer.id}
              className={`relative bg-white rounded-2xl p-8 transition-all duration-300 ${
                layer.isCore
                  ? 'shadow-2xl ring-2 ring-teal-500 scale-105 lg:scale-110'
                  : 'shadow-lg hover:shadow-xl'
              } ${
                hoveredLayer === layer.id ? 'transform -translate-y-2' : ''
              }`}
              onMouseEnter={() => setHoveredLayer(layer.id)}
              onMouseLeave={() => setHoveredLayer(null)}
            >
              {/* 核心标识 */}
              {layer.isCore && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap">
                    核心能力
                  </div>
                </div>
              )}

              {/* 层级编号 */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold ${
                    layer.isCore
                      ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white'
                      : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700'
                  }`}
                >
                  {index + 1}
                </div>
                <div
                  className={`text-sm font-medium px-3 py-1 rounded-full ${
                    layer.isCore
                      ? 'bg-teal-50 text-teal-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {layer.subtitle}
                </div>
              </div>

              {/* 层级标题 */}
              <h3
                className={`text-2xl font-bold mb-6 ${
                  layer.isCore ? 'text-teal-600' : 'text-gray-900'
                }`}
              >
                {layer.title}
              </h3>

              {/* 能力列表 */}
              <div className="space-y-3">
                {layer.capabilities.map((capability, capIndex) => (
                  <div
                    key={capIndex}
                    className="flex items-start gap-3 group"
                  >
                    <div
                      className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        layer.isCore
                          ? 'bg-teal-100 text-teal-600 group-hover:bg-teal-500 group-hover:text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-300'
                      }`}
                    >
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <span
                      className={`text-base leading-relaxed ${
                        layer.isCore
                          ? 'text-gray-800 font-medium'
                          : 'text-gray-700'
                      }`}
                    >
                      {capability}
                    </span>
                  </div>
                ))}
              </div>

              {/* 装饰性底部渐变 */}
              <div
                className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl ${
                  layer.isCore
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500'
                    : 'bg-gradient-to-r from-gray-200 to-gray-300'
                }`}
              ></div>
            </div>
          ))}
        </div>

        {/* 底部说明 */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-white rounded-xl px-6 py-4 shadow-md">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-white text-lg"></i>
            </div>
            <p className="text-gray-700 text-left">
              <span className="font-semibold text-gray-900">系统层</span>
              为核心，工具层提升效率，增强层应对复杂场景
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}